/* eslint-disable react/no-danger -- The one sanctioned raw-HTML render site in
   the client (spec #292). The `react/no-danger` gate in `eslint.config.js` is
   an error everywhere else precisely so this file stays the only exception; the
   safety contract that earns it is stated at the call below.

   File-scoped, as spec #292 specifies, so the reasoning lives with the
   component rather than trailing one JSX attribute. The cost is that a
   *second* raw-HTML render added to this file would not be flagged — so
   don't: this component renders the output of `sanitizeRichText` and nothing
   else, and a new markup surface belongs behind its own review of the
   boundary, not behind this exemption. */

import { type ComponentPropsWithoutRef, useMemo } from "react";

import { RICH_TEXT_PAINT, RICH_TEXT_READ_PAINT } from "@/components/ui/rich-text-paint";
import { sanitizeRichText } from "@/components/ui/sanitize-rich-text";
import { cn } from "@/lib/utils";

// `@modsy/react-ui`'s `HtmlBox` (`packages/react-ui/src/html-box/`): authored
// markup, rendered as markup. The donor draws an item's description through it
// on the list row, and the specifications screens draw their rich text through
// it too — so it is shared here, and the `react/no-danger` exemption that the
// catalog's `item-description.tsx` carried moved with it (#657).
//
// **This is the one place in the client that renders HTML rather than text**,
// so the contract behind it is worth stating where the call is:
//
//   - **It sanitises on render** (#724, D3), once per `html`:
//     `sanitizeRichText` (`sanitize-rich-text.ts`) runs Python's policy — the
//     stored tag allowlist, `href`/`target` on links only, a `rel` stamped on
//     each, no `style`, no `img`, no `javascript:` — in front of whatever the
//     caller hands over. Exactly Python's: the five extra tags the donor's
//     editor can keep from a paste are unwrapped too (#741 D2). The donor's `HtmlBox` sanitises nothing at all, which made a
//     community link's description and a Vis Spec line's notes stored XSS;
//     that is the one thing about it deliberately not ported.
//   - Python's `sanitize_rich_text` stays the stored-data boundary where a
//     read has one (the catalog description), and on that already-clean input
//     this pass is idempotent — so nothing that renders today changes.
//   - The box does not decide presence. Markup that sanitises to nothing
//     renders an empty box; a caller that needs that decision calls
//     `sanitizeRichText` itself, and `null`-on-empty stays Python's rule for
//     the fields it serves.
//
// **The donor's rendering is user-agent defaults, and that is the spec.** MUI's
// `CssBaseline` resets exactly four things — `html`, `box-sizing`, `strong, b`,
// and `body` (`@mui/material/CssBaseline/CssBaseline.js:63-80`) — and the donor
// theme's only override touches `body` (`theme.ts:298-303`). No `len-html-box`
// rule exists anywhere in the donor. So every heading, paragraph, list and link
// in a modsy description renders at the browser's own defaults, and preflight
// strips every one of them. `RICH_TEXT_PAINT` puts them back, in `em` where the
// UA uses `em`, rather than the nearest comfortable Tailwind step — a
// description is migrated content, and it should look like it did.
//
// Anchors included: they are unstyled in the donor too, so they render in the
// browser's own link colours here. That was open on #337 while only the read
// side had been ported — a house-navy link here and a user-agent-blue one in
// the editor — and #410 closed it by moving the whole set into
// `RICH_TEXT_PAINT`, which both surfaces now wear.
//
// **Type, ink and geometry are the caller's.** The donor sizes and inks every
// `HtmlBox` at the call site — the list row's column is `body2` in
// `text.secondary` with a 400px floor (`item-data-row.tsx:228-236`), the
// details page's description is `body2` in `text.primary`
// (`Description.tsx:22-25`) — so the box itself carries only the markup's
// restorations.

export interface HtmlBoxProps extends Omit<
  ComponentPropsWithoutRef<"div">,
  "children" | "dangerouslySetInnerHTML"
> {
  /** Stored rich text. Sanitised here, on render, before it is drawn. */
  html: string;
}

export function HtmlBox({ html, className, ...props }: HtmlBoxProps) {
  const safe = useMemo(() => sanitizeRichText(html), [html]);
  return (
    <div
      data-slot="html-box"
      {...props}
      className={cn(
        // Every UA restoration the editor also wears, so the same description
        // cannot change shape between being edited and being read.
        RICH_TEXT_PAINT,
        // …and the one it does not: `p { margin: 1em 0 }`, which is the read
        // side's alone because draft-js's editor renders a paragraph as a bare
        // `<div>` and shows no spacing at all. See the constant.
        RICH_TEXT_READ_PAINT,
        className,
      )}
      // Safe by the contract above: the string is `sanitizeRichText`'s output
      // for this very `html`. Never point this at anything else.
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}
